import * as Cesium from 'cesium';
import { getAltitudeColor } from '../utils/colors.js';
import { getAircraftType } from '../utils/aircraftType.js';
import { createAircraftModelUris } from './aircraftModels.js';

export { getAircraftType };

// Prozedural erzeugte 3D-Modelle (Blob-URLs, einmal pro Session gebaut)
const MODEL_URIS = createAircraftModelUris();

// Mindest-Pixelgröße der 3D-Modelle (~+50 % gegenüber den 2D-Icons) und
// Skalierungsdeckel, damit weit herausgezoomte Modelle nicht absurd wachsen.
// Basis-Pixelgröße (Normalzustand): bewusst dezent/klein & hochwertig — nur
// wenig über den 2D-Icons. Der Wachstumsdeckel bleibt großzügig.
const MODEL_MIN_PIXELS = { airplane: 60, jet: 51, helicopter: 48, smallplane: 42, glider: 46 };
const MODEL_MAX_SCALE = { airplane: 300, jet: 450, helicopter: 500, smallplane: 700, glider: 700 };

// Beim Heranzoomen wachsen die 3D-Modelle sanft mit ("Apple-Style"): Weil die
// Modelle real winzig sind, dominiert sonst minimumPixelSize und hält sie über
// den gesamten Zoombereich konstant groß. Wir blenden die effektive
// Pixelgröße daher distanzabhängig — nah = etwas größer, fern = Basisgröße —
// mit einer weichen Smoothstep-Kurve. Distanzen: Kamera → Flugzeug in Metern.
// Bewusst dezent: über den größten Teil des Zoombereichs bleibt die Basisgröße,
// erst bei echter Nähe wächst das Modell moderat an.
const MODEL_ZOOM_NEAR_DIST = 2.0e3;   // erst ab ~2 km volle (moderate) Größe
const MODEL_ZOOM_FAR_DIST = 1.6e4;    // schon ab ~16 km wieder Basisgröße
const MODEL_ZOOM_NEAR_FACTOR = 1.7;   // nah nur ~1,7× die Basisgröße
// Kleinere Änderungen ignorieren, damit die Szene bei ruhender Kamera
// (requestRenderMode) nicht endlos neu rendert.
const MODEL_ZOOM_EPSILON = 0.4;

// Höhenfarbe wird ins Modell gemischt (0 = Originalfarben, 1 = Vollton)
const MODEL_TINT_AMOUNT = 1.0;

// Modelle sind glTF-konform mit Nase +Z gebaut; Cesium richtet das auf
// +X ("forward") aus. Kompass-Heading 0° = Nord entspricht damit HPR-Heading
// −90° (bei HPR 0 zeigt +X nach Osten).
const MODEL_HEADING_OFFSET_DEG = -90;

// Fluglage-Grenzen: Querneigung in Kurven, Nickwinkel beim Steigen/Sinken
const MAX_BANK_RAD = Cesium.Math.toRadians(35);
const MAX_PITCH_RAD = Cesium.Math.toRadians(14);
const ATTITUDE_SMOOTHING_TAU = 0.6; // Sekunden (exponentielle Glättung)

// Moderne SVG-Icons — weiße Silhouetten mit dunklem Rand, klar unterscheidbare
// Formen (Pfeilflügel-Jet, Rotor-X-Heli, Rechteckflügel-Propellermaschine).
// Die weiße Fläche wird über Cesium billboard.color mit der Höhenfarbe getönt.
function svgIcon(body) {
  return `data:image/svg+xml,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" width="64" height="64">${body}</svg>`
  )}`;
}

const AIRCRAFT_ICONS = {
  // Verkehrsflugzeug — gepfeilte Flügel, schlanker Rumpf, Höhenleitwerk
  airplane: svgIcon(`
    <path fill="white" stroke="#111" stroke-width="1.6" stroke-linejoin="round" d="M31.2 2.6C31.5 2.2 31.7 2 32 2c.3 0 .5.2.8.6 1.2 1.6 2 4.3 2 7.2v8.1l23.5 14.6c.5.3.7.8.7 1.3v4c0 .6-.6 1-1.1.8L34.7 32l-.9 15.1 7.4 5.6c.3.2.4.5.4.8v3.1c0 .6-.5 1-1.1.8L32 55.2l-8.5 2.2c-.6.2-1.1-.2-1.1-.8v-3.1c0-.3.1-.6.4-.8l7.4-5.6-.9-15.1-23.2 6.6c-.5.2-1.1-.2-1.1-.8v-4c0-.5.2-1 .7-1.3L29.2 17.9V9.8c0-2.9.8-5.6 2-7.2z"/>
  `),

  // Privatjet — schlanker Rumpf, T-Leitwerk, Hecktriebwerke
  jet: svgIcon(`
    <path fill="white" stroke="#111" stroke-width="1.6" stroke-linejoin="round" d="M31.1 3.4c.3-.5.6-.7.9-.7.3 0 .6.2.9.7 1.1 1.8 1.8 4.5 1.8 7.3v9.6l17.8 12.4c.4.3.6.7.6 1.2v3.4c0 .6-.6 1-1.1.8L34.6 32.3l-.7 12.9 5.2.3c.5 0 .9.5.9 1v6.4l7.1 2.6c.4.1.7.5.7 1v2.3c0 .6-.5 1-1.1.9L32 57.3l-14.7 2.4c-.6.1-1.1-.3-1.1-.9v-2.3c0-.5.3-.9.7-1l7.1-2.6v-6.4c0-.5.4-1 .9-1l5.2-.3-.7-12.9-17.4 5.8c-.5.2-1.1-.2-1.1-.8v-3.4c0-.5.2-.9.6-1.2l17.8-12.4v-9.6c0-2.8.7-5.5 1.8-7.3z"/>
  `),

  // Hubschrauber — Rotor-X über rundem Rumpf mit Heckausleger
  helicopter: svgIcon(`
    <path fill="white" stroke="#111" stroke-width="1.6" stroke-linejoin="round" d="M12.9 10.1l2.8-2.8L32 23.6 48.3 7.3l2.8 2.8-16.3 16.3v.2l16.3 16.3-2.8 2.8L32 29.4 15.7 45.7l-2.8-2.8 16.3-16.3v-.2z"/>
    <path fill="white" stroke="#111" stroke-width="1.6" stroke-linejoin="round" d="M32 13c4.8 0 8.6 5.1 8.6 12.3 0 5.5-2.3 10-5.4 11.6l-1.1 13.6H39v2.8c0 .6-.4 1-1 1H26c-.6 0-1-.4-1-1v-2.8h4.9l-1.1-13.6c-3.1-1.6-5.4-6.1-5.4-11.6C23.4 18.1 27.2 13 32 13z"/>
  `),

  // Kleinflugzeug — symmetrische Rechteckflügel, Höhenleitwerk, Propeller an
  // der Nase (ein Pfad für Rumpf+Flügel+Leitwerk, exakt um x=32 gespiegelt)
  smallplane: svgIcon(`
    <path fill="white" stroke="#111" stroke-width="1.6" stroke-linejoin="round" d="M25 4.5h14a1.5 1.5 0 0 1 0 3H25a1.5 1.5 0 0 1 0-3z"/>
    <path fill="white" stroke="#111" stroke-width="1.6" stroke-linejoin="round" d="M32 2.8c1.8 0 2.9 2.1 3.1 4.8l.4 12.9H59q2.5 0 2.5 3.5t-2.5 3.5H35.2l-.9 19H42.5q2 0 2 2.5t-2 2.5H34q-2 4.3-4 0h-8.5q-2 0-2-2.5t2-2.5h8.2l-.9-19H5q-2.5 0-2.5-3.5t2.5-3.5h23.5l.4-12.9c.2-2.7 1.3-4.8 3.1-4.8z"/>
  `),

  // Segelflugzeug — sehr lange schmale Flügel, schlanker Rumpf, T-Leitwerk
  glider: svgIcon(`
    <path fill="white" stroke="#111" stroke-width="1.6" stroke-linejoin="round" d="M32 4.6c1.3 0 2.1 1.6 2.2 3.8l.3 13 26.3 1q2 .15 2 1.7t-2 1.7l-26.4.8-.8 22.2h5.6q1.6 0 1.6 1.6t-1.6 1.6h-5.8q-1.4 2.4-2.8 0h-5.8q-1.6 0-1.6-1.6t1.6-1.6h5.6l-.8-22.2-26.4-.8q-2-.15-2-1.7t2-1.7l26.3-1 .3-13c.1-2.2.9-3.8 2.2-3.8z"/>
  `)
};

// Icon-Größe pro Typ — Größenunterschied hilft zusätzlich beim Unterscheiden
const ICON_SIZES = {
  airplane: 63,
  jet: 54,
  helicopter: 54,
  smallplane: 48,
  glider: 56
};

// Bis zu welcher Kamera-Distanz (m) die Labels — Flugbezeichnung & "▲/▼ STR"-
// Badge — eingeblendet bleiben. Praktisch unbegrenzt (500.000 km), damit sie
// aus jeder Zoomstufe bis weit jenseits der Erdkugel sichtbar bleiben.
const LABEL_MAX_DISTANCE = 5.0e8;

// Icons in der Tiefe leicht verkleinern — wirkt in der Schrägansicht
// natürlicher als konstante Pixelgröße
const ICON_SCALE_BY_DISTANCE = new Cesium.NearFarScalar(2.0e4, 1.0, 6.0e5, 0.55);

const ALT_LINE_COLOR = Cesium.Color.fromCssColorString('#1a73e8').withAlpha(0.4);

// Label-/Bodenpunkt-Farben pro Theme.
// Der Bodenpunkt selbst wird jetzt nach Höhe eingefärbt (getAltitudeColor);
// hier bleibt nur die kontraststarke Umrandung (weiß/dunkel) pro Theme.
const THEME_COLORS = {
  light: {
    labelFill: Cesium.Color.fromCssColorString('#1c2333'),
    labelOutline: Cesium.Color.WHITE,
    groundDotOutline: Cesium.Color.WHITE.withAlpha(0.85),
    strDeparture: Cesium.Color.fromCssColorString('#0b8043'),
    strArrival: Cesium.Color.fromCssColorString('#1a73e8')
  },
  dark: {
    labelFill: Cesium.Color.fromCssColorString('#f2f4fa'),
    labelOutline: Cesium.Color.fromCssColorString('#14161c'),
    groundDotOutline: Cesium.Color.fromCssColorString('#14161c').withAlpha(0.85),
    strDeparture: Cesium.Color.fromCssColorString('#4cd48a'),
    strArrival: Cesium.Color.fromCssColorString('#6ba6ff')
  }
};

// Radius-Ring (aktiver Suchradius auf der Karte) — dezenter Apple-Style-Rahmen.
const RADIUS_COLORS = {
  light: '#1a73e8',
  dark: '#6ba6ff'
};
const RADIUS_RING_ALPHA = 0.55;

// Schnellfilter „No Flybys": hoch fliegende Verkehrsflugzeuge (Reiseflug über
// dieser Höhe) ausblenden — es bleiben tief fliegende An-/Abflüge & GA-Verkehr.
const QUICK_FILTER_FLYBY_ALT = 10000; // Meter

// "Spotlight": Statt einer Füllung IM Radius wird alles AUSSERHALB abgedunkelt/
// entsättigt (neutraler grauer Schleier, kein blauer Film). Der Innenbereich
// bleibt unangetastet — dadurch wirkt er hell und detailreich, außen grau.
const RADIUS_MASK = {
  light: { color: '#5b6472', alpha: 0.34 },
  dark: { color: '#04060b', alpha: 0.6 }
};

const scratchEnuMatrix = new Cesium.Matrix4();
const scratchLocalDir = new Cesium.Cartesian3();
const scratchHpr = new Cesium.HeadingPitchRoll();

/**
 * Label-Text auf der Karte: nur der Callsign. Die Landesflagge stünde bei
 * vielen Zielen dicht an dicht und macht die Karte unruhig — sie erscheint
 * deshalb ausschließlich in der Detailansicht (ui/infoPanel.js).
 */
function labelTextFor(flight) {
  return flight.callsign;
}

/**
 * Manages flight entities in Cesium viewer
 */
export class FlightManager {
  constructor(viewer, flightStore, {
    pollIntervalMs = 5000,
    trailDurationMs = 5 * 60 * 1000,
    center = null,
    radiusKm = 50,
    iconMode = '3d'
  } = {}) {
    this.viewer = viewer;
    this.flightStore = flightStore;
    this.entities = new Map(); // flight id -> Cesium entity
    this.groundDots = new Map(); // flight id -> Bodenpunkt-Entity
    this.strBadges = new Map(); // flight id -> "▲/▼ STR"-Label-Entity
    this.spotlightBadges = new Map(); // flight id -> Spotlight-Emoji-Label
    this.spotlight = null; // data/spotlightState.js (via setSpotlightState)
    this._attitudes = new Map(); // flight id -> geglättete Fluglage (roll/pitch)
    this.iconMode = iconMode === '2d' ? '2d' : '3d'; // 2D-Icons oder 3D-Modelle
    this.trails = new Map(); // flight id -> trail primitive (scene.primitives)
    this.trailHeads = new Map(); // flight id -> letzter gezeichneter Trail-Punkt
    this.trailConnectors = new Map(); // flight id -> Verbindungslinie Trail ↔ Flugzeug
    this.livePositions = new Map(); // flight id -> aktuelle (interpolierte) Position
    this.filters = {
      hideGround: true,
      minAltitude: 0,
      maxAltitude: Infinity,
      types: { airplane: true, jet: true, helicopter: true, smallplane: true, glider: false },
      showTrails: false,
      showGroundDots: false,
      // Schnellfilter-Preset: 'all' | 'noflyby' | 'priority'
      quickFilter: 'all'
    };
    this.altitudeExaggeration = 1.5;
    this.theme = THEME_COLORS.light;

    // Interpolation state für jedes Flugzeug
    this.interpolationState = new Map(); // flight id -> { startPos, targetPos, startTime, duration, ... }
    this.pollIntervalMs = pollIntervalMs;
    // Die History hält 30 min für die Zeitleiste vor — der sichtbare Trail
    // bleibt kürzer, sonst wird der Spline-Rebuild pro Poll zu teuer
    this.trailDurationMs = trailDurationMs;

    // Zeitleiste: 0 = live, >0 = so viele ms in der Vergangenheit
    this.playbackOffsetMs = 0;

    // Höhenlinie (Boden ↔ Flugzeug) für den ausgewählten Flug
    this.selectedFlightId = null;
    this._altLinePositions = null;
    this.altLineEntity = viewer.entities.add({
      id: 'selected-altitude-line',
      show: false,
      polyline: {
        positions: new Cesium.CallbackProperty(() => this._altLinePositions, false),
        width: 1.5,
        material: new Cesium.ColorMaterialProperty(ALT_LINE_COLOR)
      }
    });

    // Aktiver Suchradius als dezenter Ring auf der Karte (Apple-Style).
    // Bewusst statische Geometrie (keine CallbackProperty) — sonst würde die
    // Szene wegen requestRenderMode dauerhaft neu rendern (Akku!). Updates
    // laufen explizit über setRadiusKm().
    this.radiusCenter = center;
    this.radiusKm = radiusKm;
    if (center) {
      const ringColor = RADIUS_COLORS.light;
      const mask = RADIUS_MASK.light;
      // Abdunkelnde Maske: großes Rechteck mit kreisförmigem Loch (= Suchradius),
      // aufs Gelände + 3D-Tiles geklemmt. Alles außerhalb wird gedämpft.
      this.radiusMask = viewer.entities.add({
        id: 'radius-mask',
        polygon: {
          hierarchy: this._computeMaskHierarchy(center, radiusKm),
          material: Cesium.Color.fromCssColorString(mask.color).withAlpha(mask.alpha),
          // Nur aufs Gelände drapieren, NICHT auf die 3D-Gebäude-Tiles: Beim
          // Neigen erzeugte die Maske auf den Bauten sonst zackige "Schatten".
          classificationType: Cesium.ClassificationType.TERRAIN
        }
      });
      this.radiusRing = viewer.entities.add({
        id: 'radius-ring',
        polyline: {
          positions: this._computeRingPositions(center, radiusKm),
          width: 2,
          clampToGround: true,
          material: Cesium.Color.fromCssColorString(ringColor).withAlpha(RADIUS_RING_ALPHA)
        }
      });
    }

    // Listen for flight updates
    flightStore.on('update', () => this.onFlightDataUpdate());
    flightStore.on('remove', (id) => this.removeEntity(id));

    // 3D-Modelle beim Zoomen sanft mitwachsen lassen. preRender feuert nur,
    // wenn ohnehin gerendert wird (Kamerabewegung) — bei ruhender Kamera nichts
    // zu tun, das schont bei requestRenderMode den Akku.
    viewer.scene.preRender.addEventListener(() => this._updateModelZoomScale());

    // Start animation loop for smooth interpolation
    this.startAnimationLoop();
  }

  /**
   * Blendet die effektive Pixelgröße der 3D-Modelle distanzabhängig: nah groß,
   * fern auf Basisgröße — mit weicher Smoothstep-Kurve, damit das Heranzoomen
   * gleichmäßig "größer werden" lässt statt hart bei minimumPixelSize zu kleben.
   * Läuft pro gerendertem Frame; Änderungen unter MODEL_ZOOM_EPSILON werden
   * ignoriert, damit die Szene bei stehender Kamera zur Ruhe kommt.
   */
  _updateModelZoomScale() {
    if (this.iconMode !== '3d') return;
    const cam = this.viewer.camera.positionWC;
    const span = MODEL_ZOOM_FAR_DIST - MODEL_ZOOM_NEAR_DIST;

    for (const [flightId, entity] of this.entities) {
      if (!entity.show || !entity.model) continue;
      const pos = this.livePositions.get(flightId);
      if (!pos) continue;

      const dist = Cesium.Cartesian3.distance(cam, pos);
      // t: 0 = fern (Basisgröße), 1 = nah (maximale Größe)
      let t = (MODEL_ZOOM_FAR_DIST - dist) / span;
      t = t < 0 ? 0 : t > 1 ? 1 : t;
      const ease = t * t * (3 - 2 * t); // Smoothstep
      const factor = 1 + (MODEL_ZOOM_NEAR_FACTOR - 1) * ease;
      const target = MODEL_MIN_PIXELS[entity._aircraftType] * factor;

      if (Math.abs(target - (entity._zoomMinPx ?? -1)) > MODEL_ZOOM_EPSILON) {
        entity._zoomMinPx = target;
        entity.model.minimumPixelSize = target;
      }
    }
  }

  /**
   * Kreis-Positionen (Ring) um ein Zentrum, dicht gesampelt für eine
   * glatte, aufs Gelände geklemmte Linie.
   */
  _computeRingPositions(center, radiusKm) {
    const r = radiusKm * 1000;
    const segments = 128;
    const latRad = (center.lat * Math.PI) / 180;
    const metersPerDegLat = 111320;
    const coords = [];
    for (let i = 0; i <= segments; i++) {
      const a = (i / segments) * 2 * Math.PI;
      const dLat = (r * Math.cos(a)) / metersPerDegLat;
      const dLon = (r * Math.sin(a)) / (metersPerDegLat * Math.cos(latRad));
      coords.push(center.lon + dLon, center.lat + dLat);
    }
    return Cesium.Cartesian3.fromDegreesArray(coords);
  }

  /**
   * Polygon-Hierarchie für die Spotlight-Maske: äußere Begrenzung ist ein
   * großzügiges Rechteck um das Zentrum (exakte Grad-Geometrie, deckt den
   * Viewport bei jedem sinnvollen Zoom ab), das Loch ist der exakte Suchradius.
   */
  _computeMaskHierarchy(center, radiusKm) {
    const latMin = Math.max(center.lat - 35, -85);
    const latMax = Math.min(center.lat + 35, 85);
    const lonMin = center.lon - 45;
    const lonMax = center.lon + 45;
    const outer = Cesium.Cartesian3.fromDegreesArray([
      lonMin, latMin, lonMax, latMin, lonMax, latMax, lonMin, latMax
    ]);
    const hole = this._computeRingPositions(center, radiusKm);
    return new Cesium.PolygonHierarchy(outer, [new Cesium.PolygonHierarchy(hole)]);
  }

  /**
   * Suchradius (km) live aktualisieren — Ring + Spotlight-Maske ziehen nach.
   */
  setRadiusKm(radiusKm) {
    this.radiusKm = radiusKm;
    if (this.radiusCenter && this.radiusRing) {
      this.radiusRing.polyline.positions =
        this._computeRingPositions(this.radiusCenter, radiusKm);
    }
    if (this.radiusCenter && this.radiusMask) {
      this.radiusMask.polygon.hierarchy =
        this._computeMaskHierarchy(this.radiusCenter, radiusKm);
    }
    this.viewer.scene.requestRender();
  }

  /**
   * Theme wechseln: Label- und Bodenpunkt-Farben aller Entities anpassen.
   */
  setTheme(themeName) {
    this.theme = THEME_COLORS[themeName] || THEME_COLORS.light;
    for (const entity of this.entities.values()) {
      entity.label.fillColor = this.theme.labelFill;
      entity.label.outlineColor = this.theme.labelOutline;
    }
    // Bodenpunkt-Füllung folgt der Höhenfarbe — nur die Umrandung pro Theme
    for (const dot of this.groundDots.values()) {
      dot.point.outlineColor = this.theme.groundDotOutline;
    }
    // STR-Badges (Start/Landung Stuttgart) in Theme-Farben nachziehen
    for (const badge of this.strBadges.values()) {
      badge.label.fillColor = badge._strTag === 'departure'
        ? this.theme.strDeparture
        : this.theme.strArrival;
      badge.label.outlineColor = this.theme.labelOutline;
    }
    // Radius-Ring in der Theme-Akzentfarbe nachziehen
    const ringColor = RADIUS_COLORS[themeName] || RADIUS_COLORS.light;
    if (this.radiusRing) {
      this.radiusRing.polyline.material =
        Cesium.Color.fromCssColorString(ringColor).withAlpha(RADIUS_RING_ALPHA);
    }
    // Spotlight-Maske: dunkler Schleier im Dark-Theme, dezenter grau im Light-Theme
    const mask = RADIUS_MASK[themeName] || RADIUS_MASK.light;
    if (this.radiusMask) {
      this.radiusMask.polygon.material =
        Cesium.Color.fromCssColorString(mask.color).withAlpha(mask.alpha);
    }
    this.viewer.scene.requestRender();
  }

  /**
   * Called when new flight data arrives from server
   * Sets up interpolation targets for smooth movement
   */
  onFlightDataUpdate() {
    const flights = this.flightStore.getAllFlights();
    const now = performance.now();

    for (const flight of flights) {
      // Verschwundene Flüge (nur noch für die Zeitleiste relevant):
      // Entity behalten, aber live ausblenden und nicht mehr interpolieren
      if (flight.gone) {
        this.interpolationState.delete(flight.id);
        if (!this.entities.has(flight.id) && flight.history?.length >= 2) {
          this.updateEntity(flight);
        }
        const entity = this.entities.get(flight.id);
        if (entity && !this.isPlayback) {
          entity.show = false;
          const dot = this.groundDots.get(flight.id);
          if (dot) dot.show = false;
          const badge = this.strBadges.get(flight.id);
          if (badge) badge.show = false;
          const spot = this.spotlightBadges.get(flight.id);
          if (spot) spot.show = false;
          this.removeTrail(flight.id);
        }
        continue;
      }

      if (this.shouldShowFlight(flight)) {
        // Wieder sichtbar nach Filterwechsel? Entity reaktivieren
        this._setFlightVisible(flight.id, true);

        // Speichere den Interpolationszustand
        const currentState = this.interpolationState.get(flight.id);

        // Interpolationsdauer = tatsächlich vergangene Zeit seit letztem Update,
        // begrenzt auf [pollIntervalMs, 2*pollIntervalMs] (verhindert Teleport-Sprünge)
        const elapsed = currentState ? now - currentState.startTime : this.pollIntervalMs;
        const duration = Math.min(Math.max(elapsed, this.pollIntervalMs), 2 * this.pollIntervalMs);

        this.interpolationState.set(flight.id, {
          // Startposition ist die aktuelle interpolierte Position oder die neue Position
          startPos: currentState ? this.getCurrentInterpolatedPosition(flight.id) : {
            lat: flight.position.lat,
            lon: flight.position.lon,
            altitude: flight.position.altitude,
            heading: flight.position.heading
          },
          // Zielposition aus den neuen Daten
          targetPos: {
            lat: flight.position.lat,
            lon: flight.position.lon,
            altitude: flight.position.altitude,
            heading: flight.position.heading
          },
          startTime: now,
          duration,
          velocity: flight.velocity, // m/s
          heading: flight.position.heading,
          verticalRate: flight.verticalRate
        });

        // Initialisiere Entity falls noch nicht vorhanden
        if (!this.entities.has(flight.id)) {
          this.updateEntity(flight);
        }
        // flightData aktuell halten (Info-Panel liest beim Klick daraus)
        const entity = this.entities.get(flight.id);
        if (entity) entity.flightData = flight;

        // STR-Badge nachziehen — die Routenauflösung kommt asynchron,
        // oft erst einige Polls nach dem ersten Auftauchen des Fluges
        this._updateStrBadge(flight);
        this._updateSpotlightBadge(flight);

        this.updateTrail(flight);
      } else {
        // Gefiltert: nur ausblenden statt löschen — das ständige Entfernen/
        // Neuanlegen ganzer Entity-Batches provoziert Rendering-Artefakte
        // (korrupte Bodenpunkte) und verwirft unnötig Auswahl + Trails
        this._setFlightVisible(flight.id, false);
      }
    }

    // Request initial render
    this.viewer.scene.requestRender();
  }

  /**
   * Flug ein-/ausblenden ohne die Entities zu zerstören.
   * Beim Ausblenden werden Trail + Interpolationszustand verworfen.
   */
  _setFlightVisible(flightId, visible) {
    const entity = this.entities.get(flightId);
    if (!entity || entity.show === visible) return;

    entity.show = visible;
    const dot = this.groundDots.get(flightId);
    if (dot) dot.show = visible && this.filters.showGroundDots;
    const badge = this.strBadges.get(flightId);
    if (badge) badge.show = visible;
    const spot = this.spotlightBadges.get(flightId);
    if (spot) spot.show = visible;

    if (!visible) {
      this.removeTrail(flightId);
      this.interpolationState.delete(flightId);
    }
  }

  /**
   * Berechnet die aktuelle interpolierte Position eines Fluges
   */
  getCurrentInterpolatedPosition(flightId) {
    const state = this.interpolationState.get(flightId);
    if (!state) return null;

    const now = performance.now();
    const elapsed = now - state.startTime;
    const t = Math.min(elapsed / (state.duration || this.pollIntervalMs), 1.0);

    // Lineare Interpolation zwischen Start und Ziel
    return {
      lat: state.startPos.lat + (state.targetPos.lat - state.startPos.lat) * t,
      lon: state.startPos.lon + (state.targetPos.lon - state.startPos.lon) * t,
      altitude: state.startPos.altitude + (state.targetPos.altitude - state.startPos.altitude) * t,
      heading: this.interpolateHeading(state.startPos.heading, state.targetPos.heading, t)
    };
  }

  /**
   * Interpoliert Heading-Winkel korrekt (kürzester Weg um 360°)
   */
  interpolateHeading(from, to, t) {
    let diff = to - from;
    // Kürzesten Weg finden
    if (diff > 180) diff -= 360;
    if (diff < -180) diff += 360;
    let result = from + diff * t;
    // Normalisieren auf 0-360
    if (result < 0) result += 360;
    if (result >= 360) result -= 360;
    return result;
  }

  /**
   * Re-evaluate all flights against current filters (used on filter changes)
   */
  updateEntities() {
    const flights = this.flightStore.getAllFlights();

    for (const flight of flights) {
      if (flight.gone) continue; // wird in onFlightDataUpdate behandelt
      if (this.shouldShowFlight(flight)) {
        if (!this.entities.has(flight.id)) {
          this.updateEntity(flight);
        } else {
          this._setFlightVisible(flight.id, true);
          // Position/Farbe auf den letzten Datenstand bringen — die flüssige
          // Interpolation übernimmt wieder der nächste Poll
          this.updateEntity(flight);
        }
        this.updateTrail(flight);
      } else {
        // Ausblenden statt löschen (siehe onFlightDataUpdate)
        this._setFlightVisible(flight.id, false);
      }
    }

    this.viewer.scene.requestRender();
  }

  /**
   * Weltraum-Richtungsvektor der Bewegung (aus Kurs) an einer Position.
   * Damit richtet sich das Billboard-Icon am tatsächlichen Flugpfad aus —
   * auch in gekippter Ansicht zeigt die Nase entlang der Spur.
   */
  _headingAxis(positionCartesian, headingDegrees) {
    const h = Cesium.Math.toRadians(headingDegrees || 0);
    Cesium.Transforms.eastNorthUpToFixedFrame(positionCartesian, undefined, scratchEnuMatrix);
    scratchLocalDir.x = Math.sin(h); // Ost
    scratchLocalDir.y = Math.cos(h); // Nord
    scratchLocalDir.z = 0;
    // Frisches Objekt: Cesium speichert die Referenz in der Entity-Property
    const axis = Cesium.Matrix4.multiplyByPointAsVector(
      scratchEnuMatrix, scratchLocalDir, new Cesium.Cartesian3()
    );
    return Cesium.Cartesian3.normalize(axis, axis);
  }

  /**
   * Fluglage (Nick-/Querneigung) aus der tatsächlich dargestellten Bewegung
   * ableiten und exponentiell glätten. Funktioniert dadurch identisch für
   * Live-Interpolation und Zeitleisten-Playback:
   *  - Querneigung: Standardschräglage aus Kurvenrate × Geschwindigkeit
   *    (tan φ = v·ω / g), begrenzt auf ±35°
   *  - Nickwinkel: Steig-/Sinkwinkel des sichtbaren (überhöhten) Pfads,
   *    begrenzt auf ±14°
   */
  _updateAttitude(flightId, pos, cartesian) {
    const now = performance.now();
    let st = this._attitudes.get(flightId);
    if (!st) {
      st = {
        time: now,
        heading: pos.heading ?? 0,
        alt: pos.altitude,
        cart: Cesium.Cartesian3.clone(cartesian),
        roll: 0,
        pitch: 0
      };
      this._attitudes.set(flightId, st);
      return st;
    }

    const dt = (now - st.time) / 1000;
    if (dt < 0.02) return st; // zu kurzes Intervall — nur Rauschen

    const heading = pos.heading ?? st.heading;
    if (dt > 3) {
      // Lücke (Tab war verborgen, Zeitleisten-Sprung): Lage neu aufsetzen
      st.time = now;
      st.heading = heading;
      st.alt = pos.altitude;
      Cesium.Cartesian3.clone(cartesian, st.cart);
      return st;
    }

    let dh = heading - st.heading;
    if (dh > 180) dh -= 360;
    if (dh < -180) dh += 360;
    const turnRate = Cesium.Math.toRadians(dh) / dt; // rad/s, + = Rechtskurve

    const dist = Cesium.Cartesian3.distance(cartesian, st.cart);
    const vAlt = ((pos.altitude - st.alt) * this.altitudeExaggeration) / dt;
    const vHoriz = Math.sqrt(Math.max((dist / dt) ** 2 - vAlt * vAlt, 0));

    const targetRoll = Cesium.Math.clamp(
      Math.atan2(vHoriz * turnRate, 9.81), -MAX_BANK_RAD, MAX_BANK_RAD
    );
    const targetPitch = Cesium.Math.clamp(
      Math.atan2(vAlt, Math.max(vHoriz, 1)), -MAX_PITCH_RAD, MAX_PITCH_RAD
    );

    const k = 1 - Math.exp(-dt / ATTITUDE_SMOOTHING_TAU);
    st.roll += (targetRoll - st.roll) * k;
    st.pitch += (targetPitch - st.pitch) * k;

    st.time = now;
    st.heading = heading;
    st.alt = pos.altitude;
    Cesium.Cartesian3.clone(cartesian, st.cart);
    return st;
  }

  /**
   * Setzt Position/Rotation/Farbe eines Fluges auf Entity + Bodenpunkt +
   * (falls ausgewählt) Höhenlinie. Gemeinsamer Pfad für Live und Playback.
   */
  _applyPosition(flightId, entity, pos) {
    const exaggeratedAlt = pos.altitude * this.altitudeExaggeration;
    const newPosition = Cesium.Cartesian3.fromDegrees(pos.lon, pos.lat, exaggeratedAlt);

    entity.position = newPosition;
    this.livePositions.set(flightId, newPosition);

    if (this.iconMode === '3d') {
      // 3D-Modell: volle Fluglage — Heading aus den Daten, Nick- und
      // Querneigung aus der beobachteten Bewegung (geglättet). Das Modell
      // legt sich damit sauber in Kurven und neigt sich beim Steigen/Sinken.
      const att = this._updateAttitude(flightId, pos, newPosition);
      scratchHpr.heading = Cesium.Math.toRadians((pos.heading ?? 0) + MODEL_HEADING_OFFSET_DEG);
      scratchHpr.pitch = att.pitch;
      scratchHpr.roll = att.roll;
      entity.orientation = Cesium.Transforms.headingPitchRollQuaternion(newPosition, scratchHpr);
      entity.model.color = getAltitudeColor(pos.altitude);
    } else if (typeof pos.heading === 'number') {
      // 2D-Icon am Bewegungsvektor ausrichten statt an der Bildschirm-
      // Nordachse: bleibt beim Kippen der Ansicht konsistent zur Flugspur
      entity.billboard.alignedAxis = this._headingAxis(newPosition, pos.heading);
      entity.billboard.rotation = 0;
      entity.billboard.color = getAltitudeColor(pos.altitude);
    } else {
      entity.billboard.alignedAxis = Cesium.Cartesian3.UNIT_Z;
      entity.billboard.rotation = 0;
      entity.billboard.color = getAltitudeColor(pos.altitude);
    }

    const badge = this.strBadges.get(flightId);
    if (badge) badge.position = newPosition;
    const spot = this.spotlightBadges.get(flightId);
    if (spot) spot.position = newPosition;

    const dot = this.groundDots.get(flightId);
    if (dot) {
      dot.position = Cesium.Cartesian3.fromDegrees(pos.lon, pos.lat, 0);
      dot.point.color = getAltitudeColor(pos.altitude);
    }

    if (flightId === this.selectedFlightId) {
      // Startpunkt der Linie auf Geländehöhe (falls Terrain geladen), sonst 0
      const carto = Cesium.Cartographic.fromDegrees(pos.lon, pos.lat);
      const groundHeight = this.viewer.scene.globe.getHeight(carto) ?? 0;
      this._altLinePositions = [
        Cesium.Cartesian3.fromDegrees(pos.lon, pos.lat, groundHeight),
        newPosition
      ];
    }
  }

  /**
   * Aktualisiert alle Entity-Positionen basierend auf Interpolation
   * Wird jeden Frame aufgerufen für flüssige Bewegung
   */
  updateInterpolatedPositions() {
    let needsRender = false;

    for (const [flightId] of this.interpolationState) {
      const entity = this.entities.get(flightId);
      if (!entity) continue;

      const interpolatedPos = this.getCurrentInterpolatedPosition(flightId);
      if (!interpolatedPos) continue;

      if (!entity.show) entity.show = true;
      this._applyPosition(flightId, entity, interpolatedPos);
      needsRender = true;
    }

    return needsRender;
  }

  /**
   * Zeitleiste: positioniert alle Flugzeuge aus ihrer History zum Zeitpunkt
   * "jetzt − playbackOffsetMs". Flüge ohne History zu diesem Zeitpunkt werden
   * ausgeblendet.
   */
  updatePlaybackPositions() {
    const targetTime = Date.now() - this.playbackOffsetMs;
    let needsRender = false;

    for (const [flightId, entity] of this.entities) {
      const flight = this.flightStore.getFlight(flightId);
      const history = flight?.history;

      const passes = flight ? this._passesFilters(flight) : false;
      const pos = passes && history ? samplePositionAt(history, targetTime) : null;
      if (!pos) {
        if (entity.show) {
          entity.show = false;
          const dot = this.groundDots.get(flightId);
          if (dot) dot.show = false;
          const badge = this.strBadges.get(flightId);
          if (badge) badge.show = false;
          const spot = this.spotlightBadges.get(flightId);
          if (spot) spot.show = false;
          needsRender = true;
        }
        continue;
      }

      if (!entity.show) {
        entity.show = true;
        const dot = this.groundDots.get(flightId);
        if (dot) dot.show = this.filters.showGroundDots;
        const badge = this.strBadges.get(flightId);
        if (badge) badge.show = true;
        const spot = this.spotlightBadges.get(flightId);
        if (spot) spot.show = true;
      }
      this._applyPosition(flightId, entity, pos);
      needsRender = true;
    }

    return needsRender;
  }

  /**
   * Zeitleiste steuern: offsetMs = 0 → live, sonst ms in der Vergangenheit.
   */
  setPlaybackOffset(offsetMs) {
    this.playbackOffsetMs = Math.max(0, offsetMs || 0);
    const playback = this.playbackOffsetMs > 0;

    // Verbindungslinien (Trail ↔ Flugzeug) sind nur live sinnvoll
    for (const [flightId, connector] of this.trailConnectors) {
      connector.show = !playback && this._shouldShowTrail(flightId);
    }

    if (!playback) {
      // Zurück zu live: sichtbare (nicht verschwundene) Flüge wieder einblenden
      for (const [flightId, entity] of this.entities) {
        const flight = this.flightStore.getFlight(flightId);
        const visible = Boolean(flight && !flight.gone && this._passesFilters(flight));
        entity.show = visible;
        const dot = this.groundDots.get(flightId);
        if (dot) dot.show = visible && this.filters.showGroundDots;
        const badge = this.strBadges.get(flightId);
        if (badge) badge.show = visible;
        const spot = this.spotlightBadges.get(flightId);
        if (spot) spot.show = visible;
      }
    }
    this.viewer.scene.requestRender();
  }

  get isPlayback() {
    return this.playbackOffsetMs > 0;
  }

  /**
   * Filterprüfung ohne "gone"-Logik (für die Zeitleiste, wo auch bereits
   * verschwundene Flüge sichtbar sein sollen).
   */
  _passesFilters(flight) {
    if (this.filters.hideGround && flight.onGround) {
      return false;
    }
    const altitude = flight.position.altitude;
    if (altitude < this.filters.minAltitude) {
      return false;
    }
    if (Number.isFinite(this.filters.maxAltitude) && altitude > this.filters.maxAltitude) {
      return false;
    }
    const type = getAircraftType(flight);
    if (!this.filters.types[type]) {
      return false;
    }
    // Schnellfilter „No Flybys": hoch fliegende Verkehrsflugzeuge ausblenden
    if (this.filters.quickFilter === 'noflyby' &&
        type === 'airplane' && altitude > QUICK_FILTER_FLYBY_ALT) {
      return false;
    }
    // Schnellfilter „Highlights": nur STR-Start/Landung + besondere Funde
    if (this.filters.quickFilter === 'priority') {
      const strTagged = flight.strTag === 'departure' || flight.strTag === 'arrival';
      const special = this.spotlight ? this.spotlight.isInteresting(flight.id) : false;
      if (!strTagged && !special) return false;
    }
    return true;
  }

  /**
   * Check if flight passes current filters (live view)
   */
  shouldShowFlight(flight) {
    return !flight.gone && this._passesFilters(flight);
  }

  /**
   * Update or create entity for a single flight
   */
  updateEntity(flight) {
    const exaggeratedAlt = flight.position.altitude * this.altitudeExaggeration;
    const position = Cesium.Cartesian3.fromDegrees(
      flight.position.lon,
      flight.position.lat,
      exaggeratedAlt
    );

    // Bestimme den Flugzeugtyp und das passende Icon
    const aircraftType = getAircraftType(flight);
    const iconUrl = AIRCRAFT_ICONS[aircraftType];
    const iconSize = ICON_SIZES[aircraftType];

    let entity = this.entities.get(flight.id);

    if (!entity) {
      // Neue Entity mit beiden Darstellungen: 2D-Billboard und 3D-Modell.
      // Sichtbar ist je nach iconMode nur eine — der Umschalter (Einstellungen)
      // toggelt lediglich die show-Flags, ohne Entities neu zu bauen.
      const heading = flight.position.heading ?? 0;
      scratchHpr.heading = Cesium.Math.toRadians(heading + MODEL_HEADING_OFFSET_DEG);
      scratchHpr.pitch = 0;
      scratchHpr.roll = 0;

      entity = this.viewer.entities.add({
        id: `flight-${flight.id}`,
        name: flight.callsign,
        position: position,
        orientation: Cesium.Transforms.headingPitchRollQuaternion(position, scratchHpr),
        billboard: {
          show: this.iconMode === '2d',
          image: iconUrl,
          width: iconSize,
          height: iconSize,
          rotation: 0,
          // Ausrichtung am Bewegungsvektor (wird pro Frame aktualisiert)
          alignedAxis: this._headingAxis(position, flight.position.heading),
          color: getAltitudeColor(flight.position.altitude), // Höhenfarbe
          scaleByDistance: ICON_SCALE_BY_DISTANCE,
          // Bodenfahrzeuge aufs Gelände klemmen (wichtig bei aktivem Terrain)
          heightReference: flight.onGround
            ? Cesium.HeightReference.CLAMP_TO_GROUND
            : Cesium.HeightReference.NONE,
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
          verticalOrigin: Cesium.VerticalOrigin.CENTER,
          horizontalOrigin: Cesium.HorizontalOrigin.CENTER
        },
        model: {
          show: this.iconMode === '3d',
          uri: MODEL_URIS[aircraftType],
          minimumPixelSize: MODEL_MIN_PIXELS[aircraftType],
          maximumScale: MODEL_MAX_SCALE[aircraftType],
          color: getAltitudeColor(flight.position.altitude),
          colorBlendMode: Cesium.ColorBlendMode.MIX,
          colorBlendAmount: MODEL_TINT_AMOUNT,
          heightReference: flight.onGround
            ? Cesium.HeightReference.CLAMP_TO_GROUND
            : Cesium.HeightReference.NONE
        },
        label: {
          text: labelTextFor(flight),
          font: '600 12px sans-serif',
          fillColor: this.theme.labelFill,
          outlineColor: this.theme.labelOutline,
          outlineWidth: 3,
          style: Cesium.LabelStyle.FILL_AND_OUTLINE,
          verticalOrigin: Cesium.VerticalOrigin.TOP,
          pixelOffset: new Cesium.Cartesian2(0, 24),
          distanceDisplayCondition: new Cesium.DistanceDisplayCondition(0, LABEL_MAX_DISTANCE),
          disableDepthTestDistance: Number.POSITIVE_INFINITY
        }
      });

      this.entities.set(flight.id, entity);
      // Typ merken — _updateModelZoomScale() liest daraus die Basisgröße
      entity._aircraftType = aircraftType;
    } else {
      // Update existing entity
      entity.position = position;
      entity.billboard.image = iconUrl; // Update falls Typ sich ändert
      entity.billboard.width = iconSize;
      entity.billboard.height = iconSize;
      entity.billboard.color = getAltitudeColor(flight.position.altitude); // Höhenfarbe aktualisieren
      entity.billboard.heightReference = flight.onGround
        ? Cesium.HeightReference.CLAMP_TO_GROUND
        : Cesium.HeightReference.NONE;
      entity.model.uri = MODEL_URIS[aircraftType]; // Update falls Typ sich ändert
      // minimumPixelSize NICHT hier setzen — das besorgt _updateModelZoomScale()
      // distanzabhängig pro Frame. Bei Typwechsel nur den gemerkten Typ und den
      // Cache zurücksetzen, damit die neue Basisgröße sofort greift.
      if (entity._aircraftType !== aircraftType) {
        entity._aircraftType = aircraftType;
        entity._zoomMinPx = undefined;
      }
      entity.model.maximumScale = MODEL_MAX_SCALE[aircraftType];
      entity.model.color = getAltitudeColor(flight.position.altitude);
      entity.model.heightReference = flight.onGround
        ? Cesium.HeightReference.CLAMP_TO_GROUND
        : Cesium.HeightReference.NONE;
      entity.label.text = labelTextFor(flight);
    }

    this.updateGroundDot(flight);
    this._updateStrBadge(flight);
    this._updateSpotlightBadge(flight);

    // Store flight data for info panel
    entity.flightData = flight;
  }

  /**
   * Kleines Label über dem Flugzeug, wenn der Flug in Stuttgart gestartet ist
   * (▲ STR) oder dort landet (▼ STR). Die Zuordnung (flight.strTag) liefert
   * der FlightDataService asynchron aus der Routen-Datenbank.
   */
  _updateStrBadge(flight) {
    const tag = flight.strTag;
    const entity = this.entities.get(flight.id);
    let badge = this.strBadges.get(flight.id);

    if (!tag || !entity) {
      if (badge) {
        this.viewer.entities.remove(badge);
        this.strBadges.delete(flight.id);
      }
      return;
    }

    const text = tag === 'departure' ? '▲ STR' : '▼ STR';
    const fill = tag === 'departure' ? this.theme.strDeparture : this.theme.strArrival;

    if (!badge) {
      const position = this.livePositions.get(flight.id) ?? Cesium.Cartesian3.fromDegrees(
        flight.position.lon,
        flight.position.lat,
        flight.position.altitude * this.altitudeExaggeration
      );
      badge = this.viewer.entities.add({
        id: `str-${flight.id}`,
        position,
        show: entity.show,
        label: {
          text,
          font: '700 11px sans-serif',
          fillColor: fill,
          outlineColor: this.theme.labelOutline,
          outlineWidth: 3,
          style: Cesium.LabelStyle.FILL_AND_OUTLINE,
          verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
          pixelOffset: new Cesium.Cartesian2(0, -22),
          distanceDisplayCondition: new Cesium.DistanceDisplayCondition(0, LABEL_MAX_DISTANCE),
          disableDepthTestDistance: Number.POSITIVE_INFINITY
        }
      });
      this.strBadges.set(flight.id, badge);
    } else if (badge._strTag !== tag) {
      badge.label.text = text;
      badge.label.fillColor = fill;
    }
    badge._strTag = tag;
  }

  /**
   * Gemeinsamen Spotlight-Zustand anbinden (data/spotlightState.js). Bei einem
   * neuen Urteil den Badge des Fluges nachziehen und — falls der Highlights-
   * Filter aktiv ist — die Sichtbarkeit neu bewerten, damit frisch markierte
   * Flüge sofort erscheinen.
   */
  setSpotlightState(state) {
    this.spotlight = state;
    state.on('change', ({ hex }) => {
      const flight = this.flightStore.getFlight(hex);
      if (flight) this._updateSpotlightBadge(flight);
      if (this.filters.quickFilter === 'priority') this.updateEntities();
    });
  }

  /**
   * Kleines Sternchen-/Emoji-Label über besonderen Flügen (Militär, Rettung,
   * selten …). Analog zu _updateStrBadge, nur höher versetzt, damit es nicht mit
   * dem „▲/▼ STR“-Badge kollidiert. Nur INTERESSANTE Urteile bekommen ein Label.
   */
  _updateSpotlightBadge(flight) {
    const verdict = this.spotlight ? this.spotlight.get(flight.id) : null;
    const entity = this.entities.get(flight.id);
    let badge = this.spotlightBadges.get(flight.id);

    if (!verdict || !verdict.interesting || !entity) {
      if (badge) {
        this.viewer.entities.remove(badge);
        this.spotlightBadges.delete(flight.id);
      }
      return;
    }

    // Nur das führende Symbol/Emoji zeigen (Label bleibt ruhig)
    const symbol = (verdict.label || '⭐').split(' ')[0] || '⭐';

    if (!badge) {
      const position = this.livePositions.get(flight.id) ?? Cesium.Cartesian3.fromDegrees(
        flight.position.lon,
        flight.position.lat,
        flight.position.altitude * this.altitudeExaggeration
      );
      badge = this.viewer.entities.add({
        id: `spot-${flight.id}`,
        position,
        show: entity.show,
        label: {
          text: symbol,
          font: '600 15px sans-serif',
          fillColor: Cesium.Color.WHITE,
          outlineColor: Cesium.Color.fromCssColorString('#14161c'),
          outlineWidth: 3,
          style: Cesium.LabelStyle.FILL_AND_OUTLINE,
          verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
          pixelOffset: new Cesium.Cartesian2(0, -40),
          distanceDisplayCondition: new Cesium.DistanceDisplayCondition(0, LABEL_MAX_DISTANCE),
          disableDepthTestDistance: Number.POSITIVE_INFINITY
        }
      });
      this.spotlightBadges.set(flight.id, badge);
    } else if (badge._symbol !== symbol) {
      badge.label.text = symbol;
    }
    badge._symbol = symbol;
  }

  /**
   * Dezenter Bodenpositions-Punkt direkt auf der Karte (leicht/dünn/transparent),
   * aufs Gelände geklemmt. Für Bodenfahrzeuge unnötig (stehen schon am Boden).
   */
  updateGroundDot(flight) {
    if (flight.onGround) {
      this.removeGroundDot(flight.id);
      return;
    }

    let dot = this.groundDots.get(flight.id);
    if (!dot) {
      dot = this.viewer.entities.add({
        id: `ground-${flight.id}`,
        position: Cesium.Cartesian3.fromDegrees(flight.position.lon, flight.position.lat, 0),
        show: this.filters.showGroundDots,
        point: {
          // Klarer, kompakter Punkt, nach Höhe eingefärbt
          pixelSize: 7,
          color: getAltitudeColor(flight.position.altitude),
          outlineColor: this.theme.groundDotOutline,
          outlineWidth: 1.5,
          // In der Ferne etwas kleiner, damit sie nicht zukleistern
          scaleByDistance: new Cesium.NearFarScalar(2.0e4, 1.0, 6.0e5, 0.55),
          heightReference: Cesium.HeightReference.CLAMP_TO_GROUND
          // Bewusst KEIN disableDepthTestDistance: bei aufs Gelände geklemmten
          // Point-Primitiven erzeugt der tiefen-lose Extra-Pass sonst
          // „verschmierte“/doppelte Bodenpunkte (Cesium-Artefakt). Mit normalem
          // Tiefentest sitzt der Punkt sauber auf der Karte.
        }
      });
      this.groundDots.set(flight.id, dot);
    } else {
      dot.point.color = getAltitudeColor(flight.position.altitude);
    }
  }

  removeGroundDot(flightId) {
    const dot = this.groundDots.get(flightId);
    if (dot) {
      this.viewer.entities.remove(dot);
      this.groundDots.delete(flightId);
    }
  }

  /**
   * Glättet die History-Punkte mit einem Catmull-Rom-Spline und liefert
   * dicht gesampelte Positionen + zugehörige Höhen für den Farbverlauf.
   */
  buildSmoothedTrail(history) {
    // Nahezu identische aufeinanderfolgende Punkte entfernen (degenerierte
    // Spline-Segmente und PolylineGeometry-Artefakte vermeiden)
    const points = [];
    const altitudes = [];
    let prev = null;
    for (const pos of history) {
      const cart = Cesium.Cartesian3.fromDegrees(
        pos.lon,
        pos.lat,
        pos.altitude * this.altitudeExaggeration
      );
      if (prev && Cesium.Cartesian3.distance(prev, cart) < 1.0) continue;
      points.push(cart);
      altitudes.push(pos.altitude);
      prev = cart;
    }

    if (points.length < 2) return null;
    if (points.length < 3) return { positions: points, altitudes };

    // Catmull-Rom-Spline durch die Messpunkte — 8 Samples pro Segment für
    // eine wirklich runde Spur (FR24-Look statt "stückeliger" Polygonzüge)
    const times = points.map((_, i) => i);
    const spline = new Cesium.CatmullRomSpline({ times, points });
    const SUBDIV = 8;

    const positions = [];
    const sampledAlts = [];
    const last = points.length - 1;
    for (let i = 0; i < last; i++) {
      for (let s = 0; s < SUBDIV; s++) {
        const t = i + s / SUBDIV;
        positions.push(spline.evaluate(t, new Cesium.Cartesian3()));
        // Höhe linear zwischen den Messpunkten interpolieren
        sampledAlts.push(altitudes[i] + (altitudes[i + 1] - altitudes[i]) * (s / SUBDIV));
      }
    }
    positions.push(points[last]);
    sampledAlts.push(altitudes[last]);

    return { positions, altitudes: sampledAlts };
  }

  /**
   * Update or create trail for a flight.
   * Primitive mit per-Vertex-Farben: jeder Trail-Abschnitt trägt die Farbe
   * der dort geflogenen Höhe (grün → gelb → rot), ältere Abschnitte blenden aus.
   *
   * Der Trail endet am vorletzten Messpunkt; das letzte Stück bis zur live
   * interpolierten Flugzeugposition übernimmt eine dynamische Verbindungslinie
   * (trailConnectors) — so "klebt" die Spur exakt am Heck des Icons.
   */
  /**
   * Ob für diesen Flug eine Spur gezeichnet werden soll: entweder global
   * über den Filter aktiviert ODER es ist der aktuell ausgewählte Flug —
   * so zeigt ein Klick immer die individuelle Spur, selbst wenn Trails im
   * Filter ausgeschaltet sind (Standard).
   */
  _shouldShowTrail(flightId) {
    return this.filters.showTrails || flightId === this.selectedFlightId;
  }

  updateTrail(flight) {
    if (!this._shouldShowTrail(flight.id)) {
      this.removeTrail(flight.id);
      return;
    }
    if (!flight.history || flight.history.length < 2) {
      return;
    }

    // Nur das jüngste Zeitfenster zeichnen (History ist zeitlich sortiert)
    const cutoff = Date.now() - this.trailDurationMs;
    let start = 0;
    while (start < flight.history.length && flight.history[start].timestamp < cutoff) {
      start++;
    }
    let window = start > 0 ? flight.history.slice(start) : flight.history;
    // Letzten Messpunkt weglassen: dorthin interpoliert das Icon gerade erst —
    // sonst ragt die Trail-Spitze vor dem Flugzeug heraus
    if (window.length > 2) {
      window = window.slice(0, -1);
    }
    if (window.length < 2) return;

    const smoothed = this.buildSmoothedTrail(window);
    if (!smoothed) return;

    const { positions, altitudes } = smoothed;
    const count = positions.length;

    // Farbe pro Vertex aus der Höhe, mit Alpha-Fade zum alten Ende hin
    const colors = new Array(count);
    for (let i = 0; i < count; i++) {
      const base = getAltitudeColor(altitudes[i]);
      const alpha = 0.2 + 0.8 * (i / (count - 1));
      colors[i] = Cesium.Color.fromAlpha(base, alpha, new Cesium.Color());
    }

    const primitive = new Cesium.Primitive({
      geometryInstances: new Cesium.GeometryInstance({
        geometry: new Cesium.PolylineGeometry({
          positions,
          width: 3.5,
          colors,
          colorsPerVertex: true,
          arcType: Cesium.ArcType.NONE
        })
      }),
      appearance: new Cesium.PolylineColorAppearance({ translucent: true }),
      asynchronous: false,
      allowPicking: false
    });

    // Alten Trail erst nach dem Hinzufügen des neuen entfernen (kein Flackern)
    const old = this.trails.get(flight.id);
    this.viewer.scene.primitives.add(primitive);
    if (old) {
      this.viewer.scene.primitives.remove(old);
    }
    this.trails.set(flight.id, primitive);
    this.trailHeads.set(flight.id, positions[count - 1]);

    this._ensureTrailConnector(flight);
  }

  /**
   * Dynamische Verbindungslinie vom Trail-Ende zur live interpolierten
   * Flugzeugposition (wird pro Frame über CallbackProperty ausgewertet).
   */
  _ensureTrailConnector(flight) {
    const flightId = flight.id;
    let connector = this.trailConnectors.get(flightId);
    const color = Cesium.Color.fromAlpha(
      getAltitudeColor(flight.position.altitude), 0.95, new Cesium.Color()
    );

    if (!connector) {
      connector = this.viewer.entities.add({
        id: `trail-head-${flightId}`,
        show: !this.isPlayback,
        polyline: {
          positions: new Cesium.CallbackProperty(() => {
            const head = this.trailHeads.get(flightId);
            const live = this.livePositions.get(flightId);
            return head && live ? [head, live] : [];
          }, false),
          width: 3.5,
          material: new Cesium.ColorMaterialProperty(color),
          arcType: Cesium.ArcType.NONE
        }
      });
      this.trailConnectors.set(flightId, connector);
    } else {
      connector.polyline.material = new Cesium.ColorMaterialProperty(color);
    }
  }

  removeTrail(flightId) {
    const trail = this.trails.get(flightId);
    if (trail) {
      this.viewer.scene.primitives.remove(trail);
      this.trails.delete(flightId);
    }
    const connector = this.trailConnectors.get(flightId);
    if (connector) {
      this.viewer.entities.remove(connector);
      this.trailConnectors.delete(flightId);
    }
    this.trailHeads.delete(flightId);
  }

  /**
   * Remove entity, ground dot and trail for a flight
   */
  removeEntity(flightId) {
    const entity = this.entities.get(flightId);
    if (entity) {
      this.viewer.entities.remove(entity);
      this.entities.delete(flightId);
    }

    this.removeGroundDot(flightId);
    this.removeTrail(flightId);

    const badge = this.strBadges.get(flightId);
    if (badge) {
      this.viewer.entities.remove(badge);
      this.strBadges.delete(flightId);
    }

    const spot = this.spotlightBadges.get(flightId);
    if (spot) {
      this.viewer.entities.remove(spot);
      this.spotlightBadges.delete(flightId);
    }

    // Interpolations- und Fluglagenzustand aufräumen
    this.interpolationState.delete(flightId);
    this.livePositions.delete(flightId);
    this._attitudes.delete(flightId);

    if (this.selectedFlightId === flightId) {
      this.setSelected(null);
    }

    this.viewer.scene.requestRender();
  }

  /**
   * Auswahl setzen — steuert die Höhenlinie Boden ↔ Flugzeug
   */
  setSelected(flightId) {
    const previous = this.selectedFlightId;
    this.selectedFlightId = flightId;
    this._altLinePositions = null;
    this.altLineEntity.show = Boolean(flightId);

    // Individuelle Flugspur an die Auswahl koppeln: Die zuvor gewählte Spur
    // entfernen (außer Trails sind global an), für die neue Auswahl sofort
    // eine Spur zeichnen — so erscheint beim Klick immer die Spur des Fluges.
    if (previous && previous !== flightId && !this.filters.showTrails) {
      this.removeTrail(previous);
    }
    if (flightId) {
      const flight = this.flightStore.getFlight(flightId);
      if (flight) this.updateTrail(flight);
    }

    this.viewer.scene.requestRender();
  }

  /**
   * Darstellung umschalten: '2d' = Billboard-Icons, '3d' = Modelle.
   * Beide Grafiken existieren an jeder Entity — nur die show-Flags kippen.
   */
  setIconMode(mode) {
    const next = mode === '2d' ? '2d' : '3d';
    if (next === this.iconMode) return;
    this.iconMode = next;
    const is3d = next === '3d';
    for (const entity of this.entities.values()) {
      entity.billboard.show = !is3d;
      entity.model.show = is3d;
    }
    if (!is3d) this._attitudes.clear(); // Fluglage beim Rückwechsel verwerfen
    this.viewer.scene.requestRender();
  }

  /**
   * Set filter options
   */
  setFilters(filters) {
    this.filters = { ...this.filters, ...filters };

    // Bodenpunkte nur ein-/ausblenden, nicht neu bauen
    if ('showGroundDots' in filters) {
      for (const dot of this.groundDots.values()) {
        dot.show = this.filters.showGroundDots;
      }
    }
    if ('showTrails' in filters && !this.filters.showTrails) {
      // Trails global ausgeschaltet: alle entfernen, nur die Spur des
      // aktuell ausgewählten Fluges bleibt bestehen.
      for (const id of [...this.trails.keys()]) {
        if (id !== this.selectedFlightId) this.removeTrail(id);
      }
    }

    this.updateEntities();
  }

  /**
   * Animation loop for smooth interpolation
   * Rendert nur wenn sich Positionen ändern (effizient)
   */
  startAnimationLoop() {
    const targetFPS = 30; // 30 FPS ist ausreichend für flüssige Bewegung
    const frameInterval = 1000 / targetFPS;
    let lastRenderTime = 0;

    const animate = (currentTime) => {
      if (!this.viewer || this.viewer.isDestroyed()) {
        return;
      }

      // Begrenze auf Ziel-FPS für bessere Performance
      const elapsed = currentTime - lastRenderTime;

      if (elapsed >= frameInterval) {
        lastRenderTime = currentTime - (elapsed % frameInterval);

        // Playback (Zeitleiste) oder Live-Interpolation
        const needsRender = this.isPlayback
          ? this.updatePlaybackPositions()
          : this.updateInterpolatedPositions();

        // Nur rendern wenn sich etwas geändert hat
        if (needsRender) {
          this.viewer.scene.requestRender();
        }
      }

      requestAnimationFrame(animate);
    };

    requestAnimationFrame(animate);
  }

  /**
   * Farbskala gewechselt: alle bereits gefärbten Elemente sofort neu einfärben.
   * Billboards + Bodenpunkte übernimmt die Interpolation im nächsten Frame,
   * die gebackenen Trails müssen jedoch neu aufgebaut werden.
   */
  refreshColors() {
    for (const entity of this.entities.values()) {
      const flight = entity.flightData;
      if (flight) {
        entity.billboard.color = getAltitudeColor(flight.position.altitude);
        entity.model.color = getAltitudeColor(flight.position.altitude);
      }
    }
    for (const [id, dot] of this.groundDots) {
      const flight = this.flightStore.getFlight(id);
      if (flight) dot.point.color = getAltitudeColor(flight.position.altitude);
    }
    for (const flight of this.flightStore.getAllFlights()) {
      const entity = this.entities.get(flight.id);
      if (this._shouldShowTrail(flight.id) && entity && entity.show && flight.history?.length >= 2) {
        this.updateTrail(flight);
      }
    }
    this.viewer.scene.requestRender();
  }

  /**
   * Get entity by flight ID
   */
  getEntity(flightId) {
    return this.entities.get(flightId);
  }
}

/**
 * Interpoliert eine Position aus der History zum Zeitpunkt t (ms epoch).
 * Liefert null, wenn die History den Zeitpunkt nicht abdeckt.
 */
function samplePositionAt(history, t) {
  if (!history.length) return null;
  // Kleine Toleranz am jungen Ende: der letzte Sample darf bis zu 10s alt sein
  if (t > history[history.length - 1].timestamp + 10000) return null;
  if (t < history[0].timestamp) return null;

  if (t >= history[history.length - 1].timestamp) {
    const last = history[history.length - 1];
    const prev = history.length > 1 ? history[history.length - 2] : last;
    return {
      lat: last.lat,
      lon: last.lon,
      altitude: last.altitude,
      heading: headingBetween(prev, last)
    };
  }

  // Bracketing-Samples suchen (History ist zeitlich sortiert)
  let lo = 0;
  let hi = history.length - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (history[mid].timestamp <= t) lo = mid;
    else hi = mid;
  }

  const a = history[lo];
  const b = history[hi];
  const span = b.timestamp - a.timestamp;
  const f = span > 0 ? (t - a.timestamp) / span : 0;

  return {
    lat: a.lat + (b.lat - a.lat) * f,
    lon: a.lon + (b.lon - a.lon) * f,
    altitude: a.altitude + (b.altitude - a.altitude) * f,
    heading: headingBetween(a, b)
  };
}

/**
 * Kurs (Grad, 0 = Nord) zwischen zwei History-Punkten.
 */
function headingBetween(a, b) {
  const dLon = (b.lon - a.lon) * Math.cos(((a.lat + b.lat) / 2) * Math.PI / 180);
  const dLat = b.lat - a.lat;
  if (dLon === 0 && dLat === 0) return 0;
  let deg = Math.atan2(dLon, dLat) * 180 / Math.PI;
  if (deg < 0) deg += 360;
  return deg;
}
